<template>
  <!-- Félicitations de Flamou -->
  <div v-if="showFlamouCongratulations" class="flamou-congratulations-overlay">
    <div class="flamou-congratulations-container">
      <img :src="flamouCongratulationsImage" alt="Flamou" class="flamou-congratulations-image" />
      <div class="flamou-speech-bubble">
        <p>{{ flamouCongratulationsMessage }}</p>
        <div class="flamou-timer" v-if="showFlamouTimer">
          <div class="flamou-timer-bar">
            <div 
              class="flamou-timer-fill"
              :style="{ width: flamouTimerProgress + '%' }"
            ></div>
          </div>
          <div class="flamou-timer-text">{{ Math.ceil(flamouTimeLeft / 1000) }}s</div>
        </div>
      </div>
    </div>
  </div>
  
  <div class="job-discovery-container">
    <!-- Animation badge débloqué -->
    <div v-if="showBadgeUnlockAnimation" class="badge-unlock-overlay">
      <div class="badge-unlock-animation">
        <img :src="badgeJobsImage" alt="Badge" class="badge-icon" />
        <h2>Badge débloqué !</h2>
        <h3>{{ badgeData.name }}</h3>
        <p>{{ badgeData.description }}</p>
        <button @click="closeBadgeAnimation" class="close-animation-btn">
          Continuer
        </button>
      </div>
    </div>
    <GameGuide
      v-if="!gameStarted"
      gameId="jobs-game"
      :forceShow="true"
      @start-game="startGame"
      @skip-intro="startGame"
    />

    <!-- Zone de jeu principale -->
    <div class="game-playground" v-if="gameStarted && !showResults">
      <!-- Barre de progression -->
      <div class="progress-container">
        <div class="progress-steps">
          <div class="progress-step" :class="{ completed: currentIndex > 0 }">
            <div class="step-icon">🎮</div>
            <div class="step-label">Démarrage</div>
          </div>

          <div class="progress-connector"></div>

          <div
            class="progress-step"
            :class="{ completed: currentIndex >= Math.floor(batchSize / 3) }"
          >
            <div class="step-icon">🏃</div>
            <div class="step-label">En cours</div>
          </div>

          <div class="progress-connector"></div>

          <div
            class="progress-step"
            :class="{
              completed: currentIndex >= Math.floor((batchSize * 2) / 3),
            }"
          >
            <div class="step-icon">🔍</div>
            <div class="step-label">Avancé</div>
          </div>

          <div class="progress-connector"></div>

          <div
            class="progress-step"
            :class="{ completed: currentIndex >= batchSize || batchCompleted }"
          >
            <div class="step-icon">🏆</div>
            <div class="step-label">Terminé</div>
          </div>
        </div>

        <div class="progress-bar">
          <div
            class="progress-fill"
            :style="{ width: progressPercentage + '%' }"
          ></div>
          <div class="progress-text">{{ currentIndex }} / {{ batchSize }}</div>
        </div>
      </div>

      <!-- Contenu du jeu -->
      <div class="game-content">
        <transition name="card-transition">
          <div v-if="currentMetier" class="job-card">
            <div class="job-card-inner">
              <h2 class="job-title">{{ currentMetier.name }}</h2>

              <div class="job-media">
                <div class="video-player">
                  <video
                    v-if="currentMetier.video"
                    ref="jobVideo"
                    :src="getVideoSource(currentMetier.video)"
                    :poster="currentMetier.poster"
                    controls
                    autoplay
                    muted
                    preload="metadata"
                    class="video-element"
                    @loadeddata="handleVideoLoaded"
                    @play="handleVideoPlay"
                    @error="handleVideoError"
                  >
                    Votre navigateur ne supporte pas la lecture de vidéos.
                  </video>
                  <div v-else class="video-placeholder">
                    <div class="placeholder-icon">🎬</div>
                    <p>Vidéo non disponible</p>
                  </div>
                </div>
              </div>

              <div class="job-description">
                <p>{{ currentMetier.description }}</p>
              </div>

              <div class="job-skills">
                <div
                  v-for="(skill, index) in currentMetier.skills"
                  :key="index"
                  class="skill-tag"
                >
                  {{ skill }}
                </div>
              </div>
            </div>
          </div>


          <div v-else-if="showRanking" class="ranking-card">
            <div class="ranking-header">
              <div class="ranking-title-container">
                <div class="ranking-icon">🏆</div>
                <h2>Classe tes métiers préférés</h2>
              </div>
              <p class="ranking-subtitle">Glisse les métiers du plus intéressant (⭐) au moins intéressant</p>
            </div>

            <!-- Cases de classement en haut -->
            <div class="ranking-slots">
              <div
                v-for="index in 5"
                :key="'slot-' + index"
                class="ranking-slot"
                @dragover.prevent
                @drop="onDropInSlot($event, index - 1)"
                :class="{ 'slot-filled': rankedBatch[index - 1] }"
              >
                <div class="slot-header">
                  <div class="slot-number">{{ index }}</div>
                  <div class="slot-label">
                    <span v-if="index === 1">⭐ Top 1</span>
                    <span v-else-if="index === 2">💫 Top 2</span>
                    <span v-else-if="index === 3">✨ Top 3</span>
                    <span v-else>N°{{ index }}</span>
                  </div>
                </div>
                <div
                  v-if="rankedBatch[index - 1]"
                  class="slot-content"
                  draggable="true"
                  @dragstart="onDragFromSlot($event, index - 1)"
                >
                  <div class="slot-job-icon">💼</div>
                  <span class="slot-job-name">{{ rankedBatch[index - 1].name }}</span>
                  <span class="slot-handle">⋮⋮</span>
                </div>
                <div v-else class="slot-placeholder">
                  <div class="placeholder-icon">👆</div>
                  <span class="placeholder-text">Glisse ici</span>
                </div>
              </div>
            </div>

            <!-- Métiers à classer en bas -->
            <div class="unranked-jobs">
              <div class="unranked-header">
                <h3>💼 Métiers à classer</h3>
                <div class="unranked-count">{{ unrankedJobs.length }} restant{{ unrankedJobs.length > 1 ? 's' : '' }}</div>
              </div>
              <div class="unranked-jobs-list">
                <div
                  v-for="job in unrankedJobs"
                  :key="job.id"
                  class="unranked-job-item"
                  draggable="true"
                  @dragstart="onDragFromUnranked($event, job)"
                  :class="{ 'dragging': draggedJob && draggedJob.id === job.id }"
                >
                  <span class="job-emoji">💼</span>
                  <span class="job-name">{{ job.name }}</span>
                  <span class="drag-icon">✋</span>
                </div>
              </div>
            </div>

            <div class="ranking-actions">
               <button
                 @click="validateRanking"
                 class="action-button ranking-validate-button"
                 :disabled="!isRankingComplete"
               >
                <span class="btn-icon">✓</span>
                <span class="btn-text">Valider mon classement</span>
              </button>
            </div>
          </div>

          <div v-else-if="batchCompleted" class="batch-complete-card">
            <div class="card-icon large-icon">🎯</div>
            <h2>Lot terminé !</h2>
            <p>Tu as découvert {{ batchSize }} métiers.</p>
            <p v-if="likedJobs.length > 0">
              Tu as aimé {{ getLikedJobsInCurrentBatch() }} métier(s).
            </p>
            <p>Souhaites-tu continuer à découvrir d'autres métiers ?</p>
          </div>

          <div v-else-if="allJobsSeen" class="all-jobs-seen-card">
            <div class="card-icon large-icon">🏁</div>
            <h2>Bravo !</h2>
            <p>Tu as découvert tous les métiers disponibles.</p>
            <p v-if="likedJobs.length > 0">
              Tu as aimé {{ likedJobs.length }} métier(s) au total.
            </p>
          </div>

          <div v-else class="loading-card">
            <div class="loading-spinner"></div>
            <p>Chargement des métiers...</p>
          </div>
        </transition>
      </div>

      <!-- Actions du jeu -->
      <div class="game-actions">
        <div v-if="currentMetier" class="card-actions">
          <button @click="dislikeJob" class="action-button dislike-button">
            <span class="btn-icon">👎</span>
            <span class="btn-text">Pas pour moi</span>
          </button>

          <button @click="likeJob" class="action-button like-button">
            <span class="btn-icon">👍</span>
            <span class="btn-text">Ça me plaît</span>
          </button>
        </div>

        <div v-if="batchCompleted" class="batch-actions">
          <button @click="loadNextBatch" class="action-button continue-button">
            <span class="btn-icon">▶️</span>
            <span class="btn-text">Continuer</span>
          </button>

          <button @click="goToNextGame" class="action-button next-game-button">
            <span class="btn-icon">🎮</span>
            <span class="btn-text">Prochain jeu</span>
          </button>

          <button
            @click="goToDashboard"
            class="action-button home-button"
          >
            <span class="btn-icon">🏠</span>
            <span class="btn-text">Retour au menu</span>
          </button>
        </div>

        <div v-if="allJobsSeen" class="home-action">
          <button
            @click="finishAndShowResults"
            class="action-button like-button"
          >
            <span class="btn-icon">❤️</span>
            <span class="btn-text">Voir mes métiers préférés</span>
          </button>

          <button @click="goToNextGame" class="action-button next-game-button">
            <span class="btn-icon">🎮</span>
            <span class="btn-text">Prochain jeu</span>
          </button>

          <button @click="goToDashboard" class="action-button home-button">
            <span class="btn-icon">🏠</span>
            <span class="btn-text">Retour au menu</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Écran des résultats -->
    <div class="results-overlay" v-if="showResults">
      <div class="results-modal">
        <div class="results-header">
          <div class="results-title-container">
            <div class="results-title-icon">🏆</div>
            <h2 class="results-title">Tes métiers préférés</h2>
          </div>
          <p class="results-subtitle">Voici les métiers qui t'ont intéressé</p>
        </div>

        <div v-if="likedJobs.length > 0" class="liked-jobs-grid">
          <div
            v-for="(job, index) in likedJobs"
            :key="index"
            class="liked-job-item"
          >
            <div class="liked-job-media">
              <img
                :src="job.poster || '@/assets/job-placeholder.jpg'"
                alt="Métier"
                class="liked-job-img"
              />
              <div class="play-overlay">▶️</div>
            </div>
            <div class="liked-job-info">
              <h3>{{ job.name }}</h3>
              <button @click="goToJobDetails(job)" class="view-button">
                <span class="btn-icon">👁️</span>
                <span class="btn-text">Voir détails</span>
              </button>
            </div>
          </div>
        </div>

        <div v-else class="no-liked-jobs">
          <div class="card-icon large-icon">🔍</div>
          <p>Tu n'as pas encore sélectionné de métiers qui te plaisent.</p>
          <p>Continue à explorer pour découvrir des métiers intéressants !</p>
        </div>

        <div class="results-actions">
          <button @click="restartGame" class="action-button restart-button">
            <span class="btn-icon">🔄</span>
            <span class="btn-text">Rejouer</span>
          </button>

          <button @click="goToNextGame" class="action-button next-game-button">
            <span class="btn-icon">🎮</span>
            <span class="btn-text">Prochain jeu</span>
          </button>

          <button @click="goToDashboard" class="action-button home-button">
            <span class="btn-icon">🏠</span>
            <span class="btn-text">Retour au menu</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { metiersData } from '@/data/metiersData.js'
import { unlockBadge, isBadgeUnlocked } from '@/utils/badges'
import GameGuide from '@/components/GameGuideComponent.vue'
import AuthService from '@/services/AuthService'
import flamouImage from '@/assets/flamou/intresting.png'
import flamouHappy from '@/assets/flamou/happy.png'
import flamouHappy2 from '@/assets/flamou/happy2.png'
import flamouNormal from '@/assets/flamou/normal.png'
import flamouHey from '@/assets/flamou/hey.png'
import badgeJobsImage from '@/assets/badges_skills/Badge_jeu_de_metiers.png'
import GameNavigationService from '@/services/GameNavigationService'

export default {
  name: 'JobDiscoveryGame',
  components: {
    GameGuide
  },
  setup() {
    const router = useRouter()
    // Variables réactives
    const gameStarted = ref(false)
    const showResults = ref(false)
    const currentIndex = ref(0)
    const currentBatch = ref([])
    const batchSize = ref(5)
    const seenJobs = ref([])
    const likedJobs = ref([])
    const currentBatchIds = ref([])
    const batchCompleted = ref(false)
    const allJobsSeen = ref(false)
    const highContrastMode = ref(false)
    const showBadgeUnlockAnimation = ref(false)
    const badgeJobDiscoveryId = ref(7)
    const jobVideo = ref(null)

    // Variables pour le classement
    const showRanking = ref(false)
    const rankedBatch = ref([null, null, null, null, null]) // 5 slots for ranking
    const draggedItem = ref(null)
    const draggedJob = ref(null)

    // Variables pour les félicitations de Flamou
    const showFlamouCongratulations = ref(false)
    const flamouCongratulationsImage = ref(flamouNormal)
    const flamouCongratulationsMessage = ref('')
    const flamouTimer = ref(null)
    const flamouTimeLeft = ref(0)
    const flamouTimerProgress = ref(100)
    const showFlamouTimer = ref(false)
    const flamouCompletionTimer = ref(null)
    const autoAdvanceTimer = ref(null)

    // Métier actuel
    const currentMetier = computed(() => {
      if (
        currentBatch.value.length === 0 ||
        currentIndex.value >= currentBatch.value.length
      ) {
        return null
      }
      return currentBatch.value[currentIndex.value]
    })

    watch(currentMetier, async (newMetier) => {
      if (newMetier && newMetier.video) {
        await nextTick() // Attendre que le DOM soit mis à jour
        tryAutoplayVideo()
      }
    })

    function tryAutoplayVideo() {
      if (jobVideo.value) {
        const video = jobVideo.value

        // Réinitialiser la vidéo
        video.currentTime = 0

        // Essayer de lancer la vidéo
        let playPromise
        try {
          playPromise = typeof video.play === 'function' ? video.play() : undefined
        } catch {
          return
        }

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
            })
            .catch(() => {
              // Si l'autoplay échoue, on peut essayer sans le son
              video.muted = true
              try {
                const mutedPlayPromise =
                  typeof video.play === 'function' ? video.play() : undefined
                if (mutedPlayPromise && typeof mutedPlayPromise.catch === 'function') {
                  mutedPlayPromise.catch(() => {
                  })
                }
              } catch {
                // Ignorer les erreurs de lecture vidéo quand l'autoplay n'est pas disponible
              }
            })
        }
      }
    }

    // Gestionnaire quand la vidéo est chargée
    function handleVideoLoaded() {
      tryAutoplayVideo()
    }

    // Gestionnaire quand la vidéo commence à jouer
    function handleVideoPlay() {
    }

    // Gestionnaire d'erreur vidéo
    function handleVideoError(event) {
      console.error('Erreur lors du chargement de la vidéo:', event)
    }

    // Modifier la fonction nextCard pour gérer l'autoplay
    function nextCard() {
      currentIndex.value++

      // Vérifier si tous les métiers du lot ont été vus
      if (currentIndex.value >= currentBatch.value.length) {
        // Passer à l'étape de classement - initialiser avec 5 slots vides
        rankedBatch.value = [null, null, null, null, null]
        showRanking.value = true
      } else {
        // Si il y a un nouveau métier, essayer l'autoplay après un court délai
        setTimeout(() => {
          tryAutoplayVideo()
        }, 100)
      }
    }

    function onDragStart(event, index) {
      draggedItem.value = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.dropEffect = 'move'
      // On passe l'index comme donnée
      event.dataTransfer.setData('text/plain', index.toString())
    }

    function onDrop(event, index) {
      event.preventDefault()
      const fromIndexStr = event.dataTransfer.getData('text/plain')
      if (!fromIndexStr) return

      const fromIndex = parseInt(fromIndexStr)
      const toIndex = index

      if (fromIndex === toIndex) return

      const item = rankedBatch.value.splice(fromIndex, 1)[0]
      rankedBatch.value.splice(toIndex, 0, item)
      draggedItem.value = null
    }

    // Nouveau système de drag & drop pour le classement
    function onDragFromUnranked(event, job) {
      draggedJob.value = job
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('jobId', job.id.toString())
      event.dataTransfer.setData('source', 'unranked')
    }

    function onDragFromSlot(event, slotIndex) {
      const job = rankedBatch.value[slotIndex]
      if (!job) return

      draggedJob.value = job
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('jobId', job.id.toString())
      event.dataTransfer.setData('source', 'slot')
      event.dataTransfer.setData('slotIndex', slotIndex.toString())
    }

    function onDropInSlot(event, targetSlotIndex) {
      event.preventDefault()

      const jobId = parseInt(event.dataTransfer.getData('jobId'))
      const source = event.dataTransfer.getData('source')

      if (!jobId) return

      let job = null

      if (source === 'unranked') {
        // Drag depuis la liste des métiers non classés
        job = currentBatch.value.find(j => j.id === jobId)
        if (!job) return

        // Si le slot cible contient déjà un métier, ne rien faire
        if (rankedBatch.value[targetSlotIndex]) return

        // Placer le métier dans le slot
        rankedBatch.value[targetSlotIndex] = job
      } else if (source === 'slot') {
        // Drag depuis un autre slot
        const sourceSlotIndex = parseInt(event.dataTransfer.getData('slotIndex'))

        // Si on essaie de déposer sur soi-même, ne rien faire
        if (sourceSlotIndex === targetSlotIndex) return

        // Si le slot cible est occupé, échanger les positions
        const targetJob = rankedBatch.value[targetSlotIndex]
        const sourceJob = rankedBatch.value[sourceSlotIndex]

        rankedBatch.value[targetSlotIndex] = sourceJob
        rankedBatch.value[sourceSlotIndex] = targetJob
      }

      draggedJob.value = null
    }

    function validateRanking() {
       if (!isRankingComplete.value) {
         return
       }

       showRanking.value = false
       batchCompleted.value = true
    }

    // Données pour le badge
    const badgeData = ref({
      name: 'Explorateur de Métiers',
      description: "Tu as exploré l'ensemble du catalogue des métiers !",
    })

    // Calcul du pourcentage de progression
    const progressPercentage = computed(() => {
      if (batchSize.value === 0) return 0
      // Faire commencer la barre à 0 et atteindre 100% au dernier élément
      return Math.min(100, (currentIndex.value / batchSize.value) * 100)
    })

    // Vérifier si le niveau est complété
    const isBatchComplete = computed(() => {
      return currentIndex.value >= currentBatch.value.length
    })

    // Métiers disponibles (non vus)
    const availableJobs = computed(() => {
      return metiersData.filter((job) => !seenJobs.value.includes(job.id))
    })

    // Métiers non encore classés (pour le ranking)
    const unrankedJobs = computed(() => {
      return currentBatch.value.filter((job) => {
        return !rankedBatch.value.some((rankedJob) => rankedJob && rankedJob.id === job.id)
      })
    })

    // Vérifier si le classement est complet
    const isRankingComplete = computed(() => {
      return rankedBatch.value.every((slot) => slot !== null)
    })

    // Fonctions
    async function startGame() {
      gameStarted.value = true
      await loadSavedData()
      loadInitialBatch()
    }

    function getVideoSource(videoPath) {
      if (!videoPath) {
        return ''
      }
      return videoPath
    }

    async function loadSavedData() {
      // 1. Charger d'abord les données du backend pour la synchronisation
      try {
        const response = await AuthService.request('get', '/games/jobs')
        if (response.data && response.data.jobChoices) {
          const choices = response.data.jobChoices
          const jobNames = Object.keys(choices)
          
          // Reconstruire seenJobs à partir des noms du backend
          const syncedSeenIds = []
          const syncedLikedJobs = []
          
          jobNames.forEach(name => {
            const job = metiersData.find(j => j.name === name)
            if (job && !syncedSeenIds.includes(job.id)) {
              syncedSeenIds.push(job.id)
              if (choices[name] === 'like') {
                syncedLikedJobs.push(job)
              }
            }
          })
          
          seenJobs.value = syncedSeenIds
          likedJobs.value = syncedLikedJobs
        }
      } catch (error) {
        console.error('Erreur lors de la synchronisation avec le backend:', error)
      }

      // 2. Charger les paramètres d'accessibilité
      const accessibilitySettings = localStorage.getItem(
        'accessibilitySettings',
      )
      if (accessibilitySettings) {
        try {
          const settings = JSON.parse(accessibilitySettings)
          highContrastMode.value = settings.highContrast || false
        } catch (e) {
          console.error(
            "Erreur lors du chargement des paramètres d'accessibilité:",
            e,
          )
        }
      }

      // 3. Fallback/Complément via localStorage pour les métiers déjà vus (si offline ou erreur backend)
      if (seenJobs.value.length === 0) {
        const seenJobsData = localStorage.getItem('seen-metiers')
        if (seenJobsData) {
          try {
            seenJobs.value = JSON.parse(seenJobsData)
          } catch (e) {
            console.error('Erreur lors du chargement des métiers vus:', e)
            seenJobs.value = []
          }
        }
      }

      // 4. Fallback/Complément via localStorage pour les métiers aimés
      if (likedJobs.value.length === 0) {
        const likedJobsData = localStorage.getItem('liked-metiers')
        if (likedJobsData) {
          try {
            const likedIds = JSON.parse(likedJobsData)
            likedJobs.value = metiersData.filter((job) =>
              likedIds.includes(job.id),
            )
          } catch (e) {
            console.error('Erreur lors du chargement des métiers aimés:', e)
            likedJobs.value = []
          }
        }
      }
    }

    function saveData() {
      // Sauvegarder les paramètres d'accessibilité
      localStorage.setItem(
        'accessibilitySettings',
        JSON.stringify({
          highContrast: highContrastMode.value,
        }),
      )

      // Sauvegarder les métiers vus
      localStorage.setItem('seen-metiers', JSON.stringify(seenJobs.value))

      // Sauvegarder les métiers aimés
      const likedIds = likedJobs.value.map((job) => job.id)
      localStorage.setItem('liked-metiers', JSON.stringify(likedIds))
    }

    function getLikedJobsInCurrentBatch() {
      return likedJobs.value.filter((job) =>
        currentBatchIds.value.includes(job.id),
      ).length
    }

    function loadInitialBatch() {
      loadNextBatch()
    }

    function loadNextBatch() {
      currentBatch.value = []
      currentIndex.value = 0
      currentBatchIds.value = []
      batchCompleted.value = false

      // Vérifier s'il reste des métiers à montrer
      if (availableJobs.value.length === 0) {
        allJobsSeen.value = true
        return
      }

      // Sélectionner aléatoirement un batch de métiers non vus
      const availBatchSize = Math.min(
        batchSize.value,
        availableJobs.value.length,
      )
      const availableIndices = Array.from(
        { length: availableJobs.value.length },
        (_, i) => i,
      )

      for (let i = 0; i < availBatchSize; i++) {
        const randomIndex = Math.floor(Math.random() * availableIndices.length)
        const selectedIndex = availableIndices.splice(randomIndex, 1)[0]

        // Créer une vraie copie profonde du métier pour éviter les problèmes de référence
        const originalMetier = availableJobs.value[selectedIndex]
        const selectedMetier = JSON.parse(JSON.stringify(originalMetier))

        currentBatch.value.push(selectedMetier)
        currentBatchIds.value.push(selectedMetier.id)
      }
    }

    function likeJob() {
      if (!currentMetier.value) return

      // Ajouter aux métiers vus
      if (!seenJobs.value.includes(currentMetier.value.id)) {
        seenJobs.value.push(currentMetier.value.id)
      }

      // Ajouter aux métiers aimés s'il n'y est pas déjà
      if (!likedJobs.value.some((job) => job.id === currentMetier.value.id)) {
        likedJobs.value.push(currentMetier.value)
      }

      // Envoyer le choix au backend
      sendJobChoiceToBackend(currentMetier.value.name, 'like')

      // Afficher les félicitations de Flamou
      displayFlamouCongratulations('like')

      saveData()
      checkBadges()
      // nextCard() will be called after the flamou message (3 seconds)
    }

    function dislikeJob() {
      if (!currentMetier.value) return

      // Ajouter uniquement aux métiers vus
      if (!seenJobs.value.includes(currentMetier.value.id)) {
        seenJobs.value.push(currentMetier.value.id)
      }

      // Envoyer le choix au backend
      sendJobChoiceToBackend(currentMetier.value.name, 'dislike')

      // Afficher les félicitations de Flamou
      displayFlamouCongratulations('dislike')

      saveData()
      checkBadges()
      // nextCard() will be called after the flamou message (3 seconds)
    }

    function unknownJob() {
      if (!currentMetier.value) return

      // Ajouter aux métiers vus
      if (!seenJobs.value.includes(currentMetier.value.id)) {
        seenJobs.value.push(currentMetier.value.id)
      }

      // Envoyer le choix au backend
      sendJobChoiceToBackend(currentMetier.value.name, 'unknown')

      saveData()
      checkBadges()
      nextCard()
    }

    function sendJobChoiceToBackend(jobName, choiceType) {
      // Mapper les types de choix aux valeurs attendues par le backend
      const choiceTypeMapping = {
        like: 'like',
        dislike: 'dislike',
        unknown: 'unknown',
        skip: 'unknown', // Si on veut gérer les sauts, on peut les mapper à 'unknown'
      }

      const backendChoice = choiceTypeMapping[choiceType]

      // D'abord, récupérer les données existantes
      AuthService.request('get', '/games/jobs')
        .then((response) => {
          // Initialiser une structure par défaut si les données sont invalides
          let currentData =
            response.data && typeof response.data === 'object'
              ? response.data
              : { completion: 0, jobChoices: {} }

          // S'assurer que la structure minimale est présente
          if (!currentData.jobChoices) {
            currentData.jobChoices = {}
          }

          // Ajouter le nouveau choix de métier
          currentData.jobChoices[jobName] = backendChoice

          // Calculer le pourcentage de complétion
          const totalPossibleJobs = metiersData.length
          const answeredJobsCount = Object.keys(currentData.jobChoices).length

          // Calculer et formater le pourcentage (0-100)
          const completionPercentage =
            totalPossibleJobs > 0
              ? Math.min(
                  100,
                  Math.round((answeredJobsCount / totalPossibleJobs) * 100),
                )
              : 0

          currentData.completion = completionPercentage / 100

          // Envoyer les données mises à jour
          return AuthService.request('post', '/games/jobs', currentData)
        })
        .catch((error) => {
          console.error(
            'Erreur lors de la mise à jour des choix de métiers:',
            error,
          )

          if (error.response) {
            console.error("Réponse d'erreur du serveur:", {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data,
            })
          }

          // En cas d'erreur d'authentification, sauvegarder localement
          if (
            error.response &&
            (error.response.status === 401 || error.response.status === 403)
          ) {
            console.warn(
              "Problème d'authentification. Vos choix de métiers seront sauvegardés localement.",
            )
            saveJobChoicesLocally(jobName, backendChoice)
          }
        })
    }

    function saveJobChoicesLocally(jobName, choiceType) {
      // Récupérer les données existantes du localStorage
      let localData = JSON.parse(
        localStorage.getItem('userJobChoices') ||
          '{"completion":0,"jobChoices":{}}',
      )

      // Mettre à jour les données locales
      if (!localData.jobChoices) {
        localData.jobChoices = {}
      }

      // Ajouter le choix de métier
      localData.jobChoices[jobName] = choiceType

      // Calculer le pourcentage de complétion
      const totalPossibleJobs = metiersData.length
      const answeredJobsCount = Object.keys(localData.jobChoices).length

      // Calculer et formater le pourcentage (0-100)
      localData.completion =
        totalPossibleJobs > 0
          ? Math.min(
              100,
              Math.round((answeredJobsCount / totalPossibleJobs) * 100),
            )
          : 0

      // Sauvegarder dans localStorage
      localStorage.setItem('userJobChoices', JSON.stringify(localData))
    }

    function showDetails() {
      // if (!currentMetier.value) return;
      // // Rediriger vers la page de détails du métier
      // window.location.href = `/metier/${currentMetier.value.id}`;
    }

    function finishAndShowResults() {
      clearAllTimers()
      showResults.value = true
      checkBadges()
    }

    function restartGame() {
      clearAllTimers()
      currentIndex.value = 0
      seenJobs.value = []
      likedJobs.value = []
      batchCompleted.value = false
      allJobsSeen.value = false
      showResults.value = false
      showFlamouCongratulations.value = false
      showFlamouTimer.value = false

      saveData()
      loadInitialBatch()
    }

    function goToDashboard() {
      GameNavigationService.returnToDashboard(router)
    }

    function goToNextGame() {
      GameNavigationService.navigateToNextGame(router, '/games/jobs')
    }

    function goToJobDetails() {
      // window.location.href = `/metier/${job.id}`;
    }

    async function checkBadges() {
      try {
        // Vérifier les badges pour les métiers aimés (Badge ID 7)
        // Seul badge à débloquer dans ce jeu
        const isJobDiscoveryUnlocked = await isBadgeUnlocked(badgeJobDiscoveryId.value)
        // Débloquer le badge quand TOUS les métiers ont été vus/répondus
        if (seenJobs.value.length >= metiersData.length && metiersData.length > 0 && !isJobDiscoveryUnlocked) {
          unlockJobDiscoveryBadge()
        }
      } catch (error) {
        console.error('Erreur lors de la vérification des badges:', error)
      }
    }

    async function unlockJobDiscoveryBadge() {
      try {
        const isUnlocked = await isBadgeUnlocked(badgeJobDiscoveryId.value)
        if (!isUnlocked) {
          const badgeUnlocked = await unlockBadge(badgeJobDiscoveryId.value)
          if (badgeUnlocked) {
            setTimeout(() => {
              showBadgeUnlockAnimation.value = true
            }, 500)
          }
        }
      } catch (error) {
        console.error('Erreur lors du déblocage du badge Job Discovery:', error)
      }
    }

    function closeBadgeAnimation() {
      showBadgeUnlockAnimation.value = false
    }

    // Démarrer le timer de Flamou
    function startFlamouTimer(duration = 3000) {
      flamouTimeLeft.value = duration
      flamouTimerProgress.value = 100
      showFlamouTimer.value = true
      
      // Nettoyer le timer existant
      if (flamouTimer.value) {
        clearInterval(flamouTimer.value)
      }
      
      flamouTimer.value = setInterval(() => {
        flamouTimeLeft.value -= 100
        flamouTimerProgress.value = (flamouTimeLeft.value / duration) * 100
        
        if (flamouTimeLeft.value <= 0) {
          clearInterval(flamouTimer.value)
          flamouTimer.value = null
          showFlamouTimer.value = false
          showFlamouCongratulations.value = false
          
          // Démarrer un délai d'attente avant de permettre la prochaine action
          startAutoAdvanceDelay()
        }
      }, 100)
    }
    
    // Démarrer le délai d'attente après la disparition de Flamou
    function startAutoAdvanceDelay() {
      autoAdvanceTimer.value = setTimeout(() => {
        // Avancer à la prochaine carte après la disparition de Flamou
        nextCard()
      }, 1000) // 1 seconde de délai après la disparition de Flamou
    }
    
    // Nettoyer tous les timers
    function clearAllTimers() {
      if (flamouTimer.value) {
        clearInterval(flamouTimer.value)
        flamouTimer.value = null
      }
      if (flamouCompletionTimer.value) {
        clearTimeout(flamouCompletionTimer.value)
        flamouCompletionTimer.value = null
      }
      if (autoAdvanceTimer.value) {
        clearTimeout(autoAdvanceTimer.value)
        autoAdvanceTimer.value = null
      }
    }

    // Fonction pour afficher les félicitations de Flamou
    function displayFlamouCongratulations(responseType) {
      let messages = []
      let image = flamouNormal
      
      if (responseType === 'like') {
        messages = [
          "Super ! 🎉",
          "J'adore ! 💕",
          "Excellent ! ✨",
          "Bravo ! 🌟",
          "Fantastique ! 🎊",
          "Génial ! 🚀",
          "Parfait ! 👍",
          "Waouh ! 🤩"
        ]
        image = Math.random() > 0.5 ? flamouHappy : flamouHappy2
      } else if (responseType === 'dislike') {
        messages = [
          "Pas de souci ! 👍",
          "C'est bien ! ✓",
          "Très bien ! 😊",
          "Parfait ! 👌",
          "Excellent ! 🎯",
          "Bien joué ! 💪",
          "Super ! 🌈",
          "Bravo ! 🏆"
        ]
        image = Math.random() > 0.5 ? flamouNormal : flamouHey
      }
      
      const randomMessage = messages[Math.floor(Math.random() * messages.length)]
      
      flamouCongratulationsMessage.value = randomMessage
      flamouCongratulationsImage.value = image
      showFlamouCongratulations.value = true
      
      // Démarrer le timer de 3 secondes
      startFlamouTimer(3000)
    }

    // Initialisation
    onMounted(() => {
      // Vérifier que metiersData est correctement importé
    })

    return {
      // État du jeu
      gameStarted,
      showRanking,
      rankedBatch,
      draggedItem,
      draggedJob,
      showResults,
      currentIndex,
      currentBatch,
      batchSize,
      seenJobs,
      likedJobs,
      currentBatchIds,
      batchCompleted,
      allJobsSeen,
      highContrastMode,

      // Données calculées
      progressPercentage,
      currentMetier,
      availableJobs,
      unrankedJobs,
      isRankingComplete,

      // Animation du badge
      showBadgeUnlockAnimation,
      badgeData,
      isBatchComplete,
      badgeJobDiscoveryId,

      // Méthodes
      startGame,
      onDragStart,
      onDrop,
      onDragFromUnranked,
      onDragFromSlot,
      onDropInSlot,
      validateRanking,
      getVideoSource,
      loadSavedData,
      saveData,
      getLikedJobsInCurrentBatch,
      loadInitialBatch,
      loadNextBatch,
      likeJob,
      dislikeJob,
      showDetails,
      nextCard,
      finishAndShowResults,
      restartGame,
      goToDashboard,
      goToNextGame,
      goToJobDetails,
      checkBadges,
      closeBadgeAnimation,
      sendJobChoiceToBackend,
      saveJobChoicesLocally,
      unknownJob,
      jobVideo,
      handleVideoLoaded,
      handleVideoPlay,
      handleVideoError,
      tryAutoplayVideo,
      flamouImage,
      badgeJobsImage,

      // Variables pour les félicitations de Flamou
      showFlamouCongratulations,
      flamouCongratulationsImage,
      flamouCongratulationsMessage,
      displayFlamouCongratulations,
      flamouTimer,
      flamouTimeLeft,
      flamouTimerProgress,
      showFlamouTimer,
      startFlamouTimer,
      clearAllTimers,
    }
  },
}
</script>

<style scoped>
/* Base Styles */
.job-discovery-container {
  font-family: 'Comic Sans MS', 'Chalkboard SE', 'Marker Felt', sans-serif;
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px;
  color: #333;
  min-height: 100vh;
}

/* Game Header */
.game-header {
  text-align: center;
  margin-bottom: 30px;
}

.main-title {
  font-size: 2.5rem;
  color: #ff6b6b;
  margin-bottom: 10px;
  text-align: center;
  position: relative;
  padding-bottom: 10px;
}

.main-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 4px;
  background-color: #4ecdc4;
  border-radius: 2px;
}

.subtitle {
  font-size: 1.2rem;
  color: #666;
  margin: 0;
}

/* Guide Character */
.guide-character {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
}

.guide-avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 3px solid #ffc107;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.speech-bubble {
  position: relative;
  background-color: #fff;
  border-radius: 15px;
  padding: 15px;
  margin-left: 15px;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1);
  max-width: 70%;
}

.speech-bubble:before {
  content: '';
  position: absolute;
  left: -10px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 10px 10px 10px 0;
  border-style: solid;
  border-color: transparent #fff transparent transparent;
}

.speech-bubble p {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
  line-height: 1.5;
}

/* Animation du badge débloqué */
.badge-unlock-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  animation: fadeIn 0.5s ease-out;
}

.badge-unlock-animation {
  background-color: #fff;
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 0 30px rgba(255, 107, 107, 0.6);
  animation: scaleIn 0.5s ease-out;
}

.badge-icon {
  width: 150px;
  height: 150px;
  margin: 0 auto 20px auto;
  display: block;
  animation: pulse 2s infinite;
}

.badge-unlock-animation h2 {
  color: #ff6b6b;
  font-size: 2rem;
  margin-bottom: 10px;
}

.badge-unlock-animation h3 {
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 15px;
}

.badge-unlock-animation p {
  color: #666;
  margin-bottom: 20px;
}

.close-animation-btn {
  background-color: #ff6b6b;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

/* Responsive pour l'animation du badge */
@media (max-width: 768px) {
  .badge-unlock-animation {
    padding: 25px;
    max-width: 85%;
    margin: 0 15px;
  }

  .badge-icon {
    width: 120px;
    height: 120px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.75rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.3rem;
  }

  .badge-unlock-animation p {
    font-size: 1rem;
  }

  .close-animation-btn {
    padding: 10px 20px;
    font-size: 1rem;
  }
}

@media (max-width: 576px) {
  .badge-unlock-animation {
    padding: 20px 15px;
    max-width: 90%;
    margin: 0 10px;
  }

  .badge-icon {
    width: 100px;
    height: 100px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.5rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.2rem;
  }

  .badge-unlock-animation p {
    font-size: 0.95rem;
  }

  .close-animation-btn {
    padding: 10px 18px;
    font-size: 0.95rem;
  }
}

@media (max-width: 400px) {
  .badge-unlock-animation {
    padding: 15px 10px;
    max-width: 95%;
  }

  .badge-icon {
    width: 80px;
    height: 80px;
  }

  .badge-unlock-animation h2 {
    font-size: 1.3rem;
  }

  .badge-unlock-animation h3 {
    font-size: 1.1rem;
  }

  .badge-unlock-animation p {
    font-size: 0.9rem;
  }

  .close-animation-btn {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
}

.close-animation-btn:hover {
  background-color: #ff5252;
  transform: scale(1.05);
}

/* Welcome Screen */
.welcome-screen {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  align-items: center;
  margin: 40px 0;
  justify-content: center;
}

.welcome-card {
  flex: 1;
  min-width: 300px;
  max-width: 600px;
  background-color: white;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.card-icon {
  font-size: 3rem;
  margin-bottom: 20px;
  color: #ff6b6b;
}

.large-icon {
  font-size: 5rem;
  margin-bottom: 20px;
}

.welcome-card h2 {
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 20px;
}

.instructions-list {
  text-align: left;
  padding-left: 10px;
  margin-bottom: 30px;
}

.instructions-list li {
  margin-bottom: 15px;
  font-size: 1.1rem;
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.instruction-step {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background-color: #ff6b6b;
  color: white;
  border-radius: 50%;
  flex-shrink: 0;
}

.sub-instructions {
  list-style-type: none;
  padding-left: 0;
  margin-top: 10px;
}

.sub-instructions li {
  margin-bottom: 8px;
  font-size: 0.95rem;
}

.start-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  margin-top: 20px;
}

.start-button:hover {
  background-color: #388e3c;
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
}

/* Game Playground */
.game-playground {
  margin-top: 30px;
}

/* Progress Container */
.progress-container {
  margin-bottom: 30px;
}

.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.step-icon {
  width: 40px;
  height: 40px;
  background-color: #e9ecef;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-bottom: 5px;
  border: 2px solid #ced4da;
  position: relative;
}

.step-label {
  font-size: 0.8rem;
  color: #6c757d;
}

.progress-step.completed .step-icon {
  background-color: #ff6b6b;
  color: white;
  border-color: #ff6b6b;
}

.progress-step.completed .step-label {
  color: #ff6b6b;
  font-weight: bold;
}

.progress-connector {
  height: 2px;
  background-color: #ced4da;
  flex-grow: 1;
  margin: 0 -5px;
  position: relative;
  top: -22px;
  z-index: 1;
}

.progress-bar {
  width: 100%;
  height: 16px;
  background-color: #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
  border-radius: 8px;
  transition: width 0.5s ease;
}

.progress-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  font-weight: bold;
  font-size: 0.75rem;
}

/* Game Content */
.game-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 30px auto;
  position: relative;
  width: 100%;
  max-width: 800px;
  min-height: 500px;
}

/* Job Card */
.job-card {
  width: 100%;
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.5s ease;
}

.job-card-inner {
  padding: 20px;
}

.job-title {
  font-size: 1.8rem;
  color: #ff6b6b;
  text-align: center;
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #f0f0f0;
}

/* Intégration de vidéo */
.job-media {
  margin-bottom: 20px;
  text-align: center;
}

.video-player {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* Ratio 16:9 */
  margin-bottom: 20px;
  border-radius: 10px;
  overflow: hidden;
  background-color: #f5f5f5;
}

.video-element {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Indicateur de lecture automatique */
.video-player::after {
  content: '▶️ Lecture automatique';
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 0.8rem;
  opacity: 0;
  animation: showAutoplayIndicator 2s ease-in-out;
}

@keyframes showAutoplayIndicator {
  0%,
  100% {
    opacity: 0;
  }
  20%,
  80% {
    opacity: 1;
  }
}

.video-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
}

/* Animation pour la transition entre vidéos */
.video-element {
  transition: opacity 0.3s ease;
}

/* Style pour les vidéos en cours de chargement */
.video-element[data-loading='true'] {
  opacity: 0.7;
}

.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 15px;
  opacity: 0.5;
}

.job-description {
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 20px;
  line-height: 1.6;
  background-color: #f9f9f9;
  padding: 15px;
  border-radius: 10px;
}

.job-skills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.skill-tag {
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: bold;
}

/* Card Actions */
.card-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
  margin-bottom: 20px;
}

.action-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.action-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.like-button {
  background-color: #4caf50;
  color: white;
}

.like-button:hover {
  background-color: #388e3c;
}

.dislike-button {
  background-color: #f44336;
  color: white;
}

.dislike-button:hover {
  background-color: #d32f2f;
}

/* Batch complete et All jobs seen cards */
.batch-complete-card,
.all-jobs-seen-card,
.loading-card {
  width: 100%;
  max-width: 600px;
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  padding: 30px;
  text-align: center;
  animation: fadeIn 0.5s ease;
}

.batch-complete-card h2,
.all-jobs-seen-card h2 {
  font-size: 2rem;
  color: #ff6b6b;
  margin-bottom: 20px;
}

.batch-complete-card p,
.all-jobs-seen-card p {
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 10px;
  line-height: 1.5;
}

.batch-actions,
.home-action {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 30px;
}

.continue-button {
  background-color: #4caf50;
  color: white;
}

.continue-button:hover {
  background-color: #388e3c;
}


/* Loading animation */
.loading-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #ff6b6b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Card Transition */
.card-transition-enter-active,
.card-transition-leave-active {
  transition: all 0.5s ease;
}

.card-transition-enter-from,
.card-transition-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

/* Results Overlay */
.results-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.5s ease;
}

.results-modal {
  background-color: white;
  border-radius: 20px;
  padding: 30px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  animation: scaleIn 0.5s ease;
}

.results-header {
  text-align: center;
  margin-bottom: 30px;
}

.results-title-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 10px;
}

.results-title-icon {
  font-size: 2.5rem;
  color: #ff6b6b;
}

.results-title {
  font-size: 2rem;
  color: #333;
  margin: 0;
}

.results-subtitle {
  font-size: 1.2rem;
  color: #666;
  margin: 0;
}

/* Liked Jobs Grid */
.liked-jobs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.liked-job-item {
  background-color: #f5f5f5;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
}

.liked-job-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.liked-job-media {
  position: relative;
  height: 140px;
  overflow: hidden;
}

.liked-job-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2.5rem;
  color: white;
  text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  opacity: 0.8;
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.liked-job-item:hover .play-overlay {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1.1);
}

.liked-job-info {
  padding: 15px;
}

.liked-job-info h3 {
  font-size: 1.1rem;
  color: #333;
  margin: 0 0 15px 0;
}

.view-button {
  display: block;
  width: 100%;
  padding: 8px 0;
  background-color: #ff6b6b;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.view-button:hover {
  background-color: #ff5252;
}

.no-liked-jobs {
  text-align: center;
  padding: 40px 20px;
  background-color: #f9f9f9;
  border-radius: 15px;
  margin-bottom: 30px;
}

.no-liked-jobs p {
  color: #666;
  font-size: 1.1rem;
  margin: 10px 0;
}

.results-actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 30px;
}

.restart-button {
  background-color: #ff9800;
  color: white;
}

.restart-button:hover {
  background-color: #f57c00;
}

.next-game-button {
  background-color: #9c27b0;
  color: white;
}

.next-game-button:hover {
  background-color: #7b1fa2;
}

.home-button {
  background-color: #607d8b;
  color: white;
}

.home-button:hover {
  background-color: #455a64;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

/* Félicitations de Flamou */
.flamou-congratulations-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1500;
  animation: fadeIn 0.3s ease-out;
}

.flamou-congratulations-container {
  display: flex;
  align-items: center;
  gap: 20px;
  animation: scaleIn 0.4s ease-out;
}

.flamou-congratulations-image {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid #ff6b6b;
  background-color: #fff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  animation: bounce 0.6s ease-out;
}

.flamou-speech-bubble {
  position: relative;
  background-color: #fff;
  border-radius: 20px;
  padding: 20px 25px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  max-width: 320px;
  border: 3px solid #ff6b6b;
}

.flamou-speech-bubble:before {
  content: '';
  position: absolute;
  left: -15px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 15px 15px 15px 0;
  border-style: solid;
  border-color: transparent #ff6b6b transparent transparent;
}

.flamou-speech-bubble:after {
  content: '';
  position: absolute;
  left: -9px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 12px 12px 12px 0;
  border-style: solid;
  border-color: transparent #fff transparent transparent;
}

.flamou-speech-bubble p {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
  line-height: 1.4;
  font-weight: bold;
  text-align: center;
}

/* Timer de Flamou */
.flamou-timer {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.flamou-timer-bar {
  width: 80px;
  height: 5px;
  background-color: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.flamou-timer-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
  border-radius: 3px;
  transition: width 0.1s linear;
}

.flamou-timer-text {
  font-size: 0.9rem;
  color: #666;
  font-weight: bold;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  25% {
    transform: translateY(-10px);
  }
  75% {
    transform: translateY(-5px);
  }
}

/* Responsive styles */
@media (max-width: 768px) {
  .card-actions {
    flex-wrap: wrap;
  }

  .action-button {
    width: 100%;
  }

  .video-player {
    padding-top: 75%; /* Ajuster le ratio pour les petits écrans */
  }

  .instructions-list li {
    align-items: flex-start;
  }

  .instruction-step {
    margin-top: 2px;
  }

  .results-modal {
    width: 95%;
    padding: 20px;
  }

  .liked-jobs-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }

  /* Félicitations de Flamou sur mobile */
  .flamou-congratulations-container {
    flex-direction: column;
    padding: 20px;
  }

  .flamou-congratulations-image {
    width: 100px;
    height: 100px;
  }

  .flamou-speech-bubble {
    max-width: 280px;
    padding: 15px 20px;
  }

  .flamou-speech-bubble:before {
    left: 50%;
    top: -15px;
    transform: translateX(-50%);
    border-width: 0 15px 15px 15px;
    border-color: transparent transparent #ff6b6b transparent;
  }

  .flamou-speech-bubble:after {
    left: 50%;
    top: -9px;
    transform: translateX(-50%);
    border-width: 0 12px 12px 12px;
    border-color: transparent transparent #fff transparent;
  }

  .flamou-speech-bubble p {
    font-size: 1.1rem;
  }
}

@media (max-width: 480px) {
  .main-title {
    font-size: 2rem;
  }

  .subtitle {
    font-size: 1rem;
  }

  .guide-character {
    flex-direction: column;
    text-align: center;
  }

  .guide-avatar {
    margin-bottom: 15px;
  }

  .speech-bubble {
    margin-left: 0;
  }

  .speech-bubble:before {
    display: none;
  }

  .progress-steps {
    display: none; /* Cacher les étapes sur très petit écran */
  }

  .liked-jobs-grid {
    grid-template-columns: 1fr;
  }

  /* Félicitations de Flamou sur très petits écrans */
  .flamou-congratulations-image {
    width: 80px;
    height: 80px;
  }

  .flamou-speech-bubble {
    max-width: 250px;
    padding: 12px 16px;
  }

  .flamou-speech-bubble p {
    font-size: 1rem;
  }
}
/* Ranking Card Styles */
.ranking-card {
  width: calc(100% + 200px);
  max-width: none;
  background: linear-gradient(135deg, #fff5f5 0%, #ffffff 50%, #f0f9ff 100%);
  border-radius: 25px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  padding: 30px 120px 40px;
  text-align: center;
  animation: fadeIn 0.5s ease;
  margin: 0 auto;
  margin-left: -100px;
  margin-right: -100px;
  min-height: 400px;
  border: 3px solid transparent;
  background-clip: padding-box;
  position: relative;
  overflow-x: auto;
}

.ranking-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 25px;
  padding: 3px;
  background: linear-gradient(135deg, #ff6b6b, #4ecdc4, #45b7d1);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0.3;
  pointer-events: none;
}

/* Header du ranking */
.ranking-header {
  margin-bottom: 30px;
}

.ranking-title-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 10px;
}

.ranking-icon {
  font-size: 3rem;
  animation: pulse 2s ease-in-out infinite;
  filter: drop-shadow(0 4px 8px rgba(255, 193, 7, 0.3));
}

.ranking-card h2 {
  font-size: 2rem;
  background: linear-gradient(135deg, #ff6b6b, #ff8e53);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  font-weight: bold;
}

.ranking-subtitle {
  font-size: 1.1rem;
  color: #555;
  margin: 0;
  font-weight: 500;
}

/* Cases de classement en haut */
.ranking-slots {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 35px;
  flex-wrap: nowrap;
  width: 100%;
}

.ranking-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #ffffff, #f8f9fa);
  border: 3px dashed #e0e0e0;
  border-radius: 16px;
  padding: 15px 8px;
  min-width: 120px;
  min-height: 120px;
  flex: 1;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
}


.ranking-slot:hover {
  border-color: #ff6b6b;
  border-style: solid;
  background: linear-gradient(135deg, #fff5f5, #ffffff);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(255, 107, 107, 0.15);
}

.ranking-slot.slot-filled {
  border-style: solid;
  border-color: #4ecdc4;
  background: linear-gradient(135deg, #e8f9f8, #ffffff);
}

.slot-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  width: 100%;
}

.slot-number {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 42px;
  height: 42px;
  background: linear-gradient(135deg, #ff6b6b, #ff8e53);
  color: white;
  border-radius: 50%;
  font-weight: bold;
  font-size: 1.2rem;
  flex-shrink: 0;
  box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
  position: relative;
}

.slot-number::after {
  content: '';
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  background: linear-gradient(135deg, #ff6b6b, #ff8e53);
  border-radius: 50%;
  z-index: -1;
  opacity: 0.3;
  filter: blur(8px);
}

.slot-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.slot-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: white;
  border: 2px solid #4ecdc4;
  border-radius: 12px;
  padding: 12px 12px;
  width: 100%;
  min-height: 60px;
  cursor: grab;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(78, 205, 196, 0.15);
}

.slot-content:hover {
  transform: translateY(-5px) scale(1.02);
  box-shadow: 0 8px 25px rgba(78, 205, 196, 0.25);
  border-color: #45b7d1;
}

.slot-content:active {
  cursor: grabbing;
  transform: scale(0.98);
  opacity: 0.8;
}

.slot-job-icon {
  font-size: 1.5rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.slot-job-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3748;
  text-align: center;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  line-height: 1.3;
  max-width: 100%;
  hyphens: auto;
}

.slot-handle {
  font-size: 1rem;
  color: #cbd5e0;
  cursor: grab;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.slot-content:hover .slot-handle {
  opacity: 1;
}

.slot-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #a0aec0;
  font-style: italic;
  text-align: center;
  padding: 15px 10px;
  line-height: 1.3;
  opacity: 0.7;
}

.placeholder-icon {
  font-size: 2rem;
  opacity: 0.5;
  animation: bounce-subtle 2s ease-in-out infinite;
}

@keyframes bounce-subtle {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

.placeholder-text {
  font-size: 0.9rem;
  font-weight: 500;
}

/* Métiers non classés en bas */
.unranked-jobs {
  margin-top: 25px;
  padding: 20px 25px;
  background: linear-gradient(135deg, #f0f9ff, #e8f5e9);
  border-radius: 16px;
  border: 2px solid rgba(78, 205, 196, 0.2);
}

.unranked-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}

.unranked-jobs h3 {
  font-size: 1.2rem;
  color: #2d3748;
  margin: 0;
  text-align: left;
  font-weight: bold;
}

.unranked-count {
  background: linear-gradient(135deg, #4ecdc4, #45b7d1);
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(78, 205, 196, 0.3);
}

.unranked-jobs-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 0;
}

.unranked-job-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #ffffff, #f0fdf4);
  border: 2px solid #4caf50;
  border-radius: 30px;
  padding: 10px 20px;
  cursor: grab;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.15);
  position: relative;
  overflow: hidden;
}


.unranked-job-item:hover {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.3);
  background: linear-gradient(135deg, #ffffff, #dcfce7);
  border-color: #22c55e;
}

.unranked-job-item:active {
  cursor: grabbing;
  transform: scale(0.95);
  opacity: 0.8;
}

.unranked-job-item.dragging {
  opacity: 0.4;
  transform: scale(0.9) rotate(5deg);
  box-shadow: 0 12px 30px rgba(76, 175, 80, 0.4);
}

.job-emoji {
  font-size: 1.3rem;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.job-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e7e34;
  letter-spacing: 0.2px;
}

.drag-icon {
  font-size: 1.1rem;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.unranked-job-item:hover .drag-icon {
  opacity: 1;
}

.ranking-actions {
  display: flex;
  justify-content: center;
  margin-top: 25px;
}

.ranking-validate-button {
  background: linear-gradient(135deg, #4caf50, #45b7d1) !important;
  color: white;
  padding: 14px 40px !important;
  font-size: 1.1rem !important;
  border-radius: 50px !important;
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  border: none;
  position: relative;
  overflow: hidden;
}


.ranking-validate-button:hover {
  transform: translateY(-3px) scale(1.05) !important;
  box-shadow: 0 10px 30px rgba(76, 175, 80, 0.4) !important;
}

.ranking-validate-button:active {
  transform: translateY(-1px) scale(1.02) !important;
}

.ranking-validate-button .btn-icon {
  font-size: 1.3rem;
  margin-right: 8px;
}

.ranking-validate-button:disabled {
  background: linear-gradient(135deg, #cbd5e0, #a0aec0) !important;
  cursor: not-allowed;
  opacity: 0.6;
  box-shadow: none !important;
}

.ranking-validate-button:disabled:hover {
  transform: none !important;
  box-shadow: none !important;
}

/* Responsive pour le ranking */
@media (max-width: 1400px) {
  .ranking-card {
    padding: 25px 80px 35px;
    width: calc(100% + 160px);
    margin-left: -80px;
    margin-right: -80px;
  }

  .ranking-slots {
    gap: 15px;
  }
}

@media (max-width: 1200px) {
  .ranking-card {
    padding: 20px 60px 30px;
    width: calc(100% + 120px);
    margin-left: -60px;
    margin-right: -60px;
  }

  .ranking-slots {
    gap: 12px;
  }

  .ranking-icon {
    font-size: 2.5rem;
  }

  .ranking-card h2 {
    font-size: 1.8rem;
  }
}

@media (max-width: 768px) {
  .ranking-card {
    padding: 20px 30px 25px;
    width: calc(100% + 40px);
    margin-left: -20px;
    margin-right: -20px;
  }

  .ranking-header {
    margin-bottom: 20px;
  }

  .ranking-icon {
    font-size: 2rem;
  }

  .ranking-card h2 {
    font-size: 1.5rem;
  }

  .ranking-subtitle {
    font-size: 0.95rem;
  }

  .ranking-slots {
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 25px;
  }

  .ranking-slot {
    min-width: 160px;
    min-height: 100px;
    padding: 12px 10px;
  }

  .slot-number {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
  }

  .slot-label {
    font-size: 0.75rem;
  }

  .slot-job-name {
    font-size: 0.85rem;
  }

  .unranked-jobs {
    padding: 15px 20px;
  }

  .unranked-jobs h3 {
    font-size: 1.1rem;
  }
}

@media (max-width: 480px) {
  .ranking-card {
    padding: 15px 15px 20px;
    width: calc(100% + 20px);
    margin-left: -10px;
    margin-right: -10px;
  }

  .ranking-title-container {
    flex-direction: column;
    gap: 8px;
  }

  .ranking-icon {
    font-size: 2.5rem;
  }

  .ranking-card h2 {
    font-size: 1.3rem;
  }

  .ranking-subtitle {
    font-size: 0.85rem;
  }

  .ranking-slots {
    gap: 8px;
    margin-bottom: 20px;
  }

  .ranking-slot {
    min-width: 120px;
    min-height: 90px;
    padding: 10px 8px;
    gap: 6px;
  }

  .slot-number {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }

  .slot-label {
    font-size: 0.7rem;
  }

  .slot-content {
    padding: 10px 8px;
    gap: 6px;
    min-height: 50px;
  }

  .slot-job-icon {
    font-size: 1.2rem;
  }

  .slot-job-name {
    font-size: 0.8rem;
    line-height: 1.2;
  }

  .slot-placeholder {
    padding: 10px 6px;
  }

  .placeholder-icon {
    font-size: 1.5rem;
  }

  .placeholder-text {
    font-size: 0.75rem;
  }

  .unranked-jobs {
    padding: 12px 15px;
    margin-top: 20px;
  }

  .unranked-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .unranked-jobs h3 {
    font-size: 1rem;
  }

  .unranked-count {
    font-size: 0.75rem;
    padding: 3px 10px;
  }

  .unranked-job-item {
    padding: 8px 14px;
  }

  .job-emoji {
    font-size: 1.1rem;
  }

  .job-name {
    font-size: 0.85rem;
  }

  .drag-icon {
    font-size: 1rem;
  }

  .ranking-validate-button {
    padding: 12px 30px !important;
    font-size: 1rem !important;
  }

  .ranking-validate-button .btn-icon {
    font-size: 1.1rem;
  }
}

</style>
